/**
 * Dimensional pivot for ONE Plan View — the selected org node(s)' consolidated budget shaped by that
 * view's row dimensions (Header Type outermost, then the view's dims), with the view's measures
 * (Volym/Pris/Värde + units). Rendered as a nested MUI table with group subtotals + collapse, matching
 * the "By period & node" tables (read-only). The page shows one of these per Plan View tab.
 */

import { useMemo, useState } from 'react';
import {
  Alert, Box, CircularProgress, Paper, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { formatNumber } from '@/lib/locale';
import { buildRollupRows, isHiddenByCollapse, RollupDisplayRow, RollupLeaf } from './rollupTree';
import { measureFormatOptions } from './planEntryRows';
import { RollupUnfilteredTotalRow, useFilteredTotalLabel } from './RollupFilteredTotal';
import { EMPTY_SHAPING, shapingQueryKey, shapingRequestFields, type RollupShaping } from './planRollupShaping';

interface PivotKey { valueCode: string; valueName: string }
/** One per-unit quantity of a row — sparse, aligned to the DTO's quantityColumns (null id = unitless). */
interface PivotUnitQuantity { unitOfMeasureId?: string | null; quantity: number }
interface PivotRow { key: PivotKey[]; amount: number; quantities?: PivotUnitQuantity[]; unitPrice?: number | null }
interface PivotAxis { dimensionDefinitionId: string; code: string; name: string }
/** A unit actually present in the scope — one Volym column each (data-driven, spec §12.4). */
interface PivotQuantityColumn {
  unitOfMeasureId?: string | null;
  unitCode?: string | null;
  unitName?: string | null;
  totalQuantity: number;
}
interface PivotMeasure {
  label: string;
  unit?: string | null;
  storageBinding: string;
  sortOrder: number;
  /** The measure's value type — the fallback when displayDecimals is not configured. */
  valueType?: string;
  /** Decimals configured on the measure in the Plan View admin; null = the value type's default. */
  displayDecimals?: number | null;
}
interface PlanRollupPivotDto {
  planViewId?: string | null;
  axes: PivotAxis[];
  measures: PivotMeasure[];
  quantityColumns: PivotQuantityColumn[];
  rows: PivotRow[];
  grandTotal: number;
  /** Whether a filter or period range narrowed this read — the server's answer, not the request's (§11). */
  isShaped: boolean;
  warnings: string[];
}

// POST like every other rollup read: the filter AST has no upper bound and does not belong in a request
// line, and every tab must narrow the same way or the tabs stop being comparable (§6, §9.1).
const PIVOT_ENDPOINT = '/api/finance/planbudgetrollup/pivot';

// Which stored PlanLine field a measure's storage binding reads — the closed set this read can
// aggregate. This is the whole of the column mapping: the columns themselves come from the Plan
// View's measures (labels, units, decimals are the view's declaration, never this component's), so a
// measure bound to something absent here (INTERNAL_REVENUE_LINE, a Plan Entry display column) simply
// gets no column in the rollup.
type Field = 'quantity' | 'rate' | 'amount';
const BINDING: Record<string, Field> = {
  PLANLINE_QUANTITY: 'quantity',
  PLANLINE_UNITPRICE: 'rate',
  PLANLINE_AMOUNT: 'amount',
};

// A rendered column. A quantity-bound measure fans out into one column per unit actually present in
// the scope (spec §12.4 — `key` carries the unit so React keys stay stable as units come and go);
// rate- and amount-bound measures stay single columns.
type PivotCol =
  | { kind: 'quantity'; key: string; unitId: string | null; header: string; format: Intl.NumberFormatOptions }
  | { kind: 'rate'; key: 'rate'; header: string; format: Intl.NumberFormatOptions }
  | { kind: 'amount'; key: 'amount'; header: string; format: Intl.NumberFormatOptions };
// Labels are translation keys — resolved where the fallback measures are used (server measures
// arrive with their own display labels and are rendered untranslated).
const DEFAULT_MEASURES: PivotMeasure[] = [
  { label: 'Finance.PlanRollup.MeasureVolume', storageBinding: 'PLANLINE_QUANTITY', sortOrder: 1, valueType: 'QUANTITY' },
  { label: 'Finance.PlanRollup.MeasurePrice', storageBinding: 'PLANLINE_UNITPRICE', sortOrder: 2, valueType: 'RATE' },
  { label: 'Finance.PlanRollup.MeasureValue', storageBinding: 'PLANLINE_AMOUNT', sortOrder: 3, valueType: 'AMOUNT' },
];

const fmt = (v: number | null | undefined, options: Intl.NumberFormatOptions) =>
  v == null ? '' : formatNumber(v, options);

interface Props {
  versionId: string;
  orgUnitIds: string[];
  excludeOrgUnitIds?: string[];
  inclusion: 'SUBMITTED' | 'ALL';
  planViewId: string;
  /** The page-level filter and period window — every tab narrows on the same one (§9.1). */
  shaping?: RollupShaping;
  /** The page's unshaped consolidated total, printed beside a filtered one as the reference (§11). */
  unfilteredTotal?: number;
}

export function PlanRollupPivot({
  versionId,
  orgUnitIds,
  excludeOrgUnitIds = [],
  inclusion,
  planViewId,
  shaping = EMPTY_SHAPING,
  unfilteredTotal,
}: Props) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const filteredLabel = useFilteredTotalLabel();

  const { data, isFetching, isError, error } = useQuery<PlanRollupPivotDto, Error>({
    queryKey: [
      'planRollupPivot', versionId, [...orgUnitIds].sort(), [...excludeOrgUnitIds].sort(), inclusion, planViewId,
      shapingQueryKey(shaping),
    ],
    queryFn: async () => {
      return (await apiClient.post<PlanRollupPivotDto>(PIVOT_ENDPOINT, {
        planVersionId: versionId,
        orgUnitIds,
        excludeOrgUnitIds,
        inclusion,
        planViewId,
        ...shapingRequestFields(shaping),
      })).data;
    },
    enabled: !!versionId && !!planViewId && orgUnitIds.length > 0,
  });

  const axes = data?.axes ?? [];
  const usingFallbackMeasures = !data?.measures?.length;
  const measures = data?.measures?.length ? data.measures : DEFAULT_MEASURES;
  const quantityColumns = useMemo(
    () => (data?.quantityColumns ?? []).map((c) => ({ ...c, unitId: c.unitOfMeasureId ?? null })),
    [data],
  );
  const measureCols = useMemo(
    () => {
      // The amount measure's free-text unit ("kr") — the numerator of a derived rate header.
      const valueUnit = measures.find((m) => BINDING[m.storageBinding] === 'amount')?.unit ?? '';
      return measures.flatMap((m): PivotCol[] => {
        const field = BINDING[m.storageBinding];
        if (!field) return [];
        const label = usingFallbackMeasures ? t(m.label) : m.label;
        // The measure declares its own decimals (Plan View admin), with the value type's default
        // behind it — resolved by the SAME helper the entry grid uses, so the two screens can
        // never disagree about how a number is written.
        const format = measureFormatOptions({ valueType: m.valueType, displayDecimals: m.displayDecimals });
        if (field === 'quantity') {
          // One column per unit actually present (spec §12.4). Header precedence: the row data's unit
          // code; for the unitless bucket the measure's free-text unit when it is the sole column
          // (legacy views seeded the unit as text while their lines carry none), else an explicit
          // "no unit" marker — the bucket holds quantities whose unit was never stamped, any unit at
          // all, and a bare label would pose as the plain quantity column beside the real units. No
          // columns (empty scope) keeps one column with today's header so the empty table still has
          // its layout.
          if (quantityColumns.length === 0) {
            return [{ kind: 'quantity', key: 'quantity', unitId: null, header: m.unit ? `${label} (${m.unit})` : label, format }];
          }
          return quantityColumns.map((c) => {
            const unitText = c.unitCode
              ?? (quantityColumns.length === 1 && m.unit ? m.unit : null)
              ?? t('Finance.PlanRollup.NoUnitColumn', { defaultValue: 'no unit' });
            return {
              kind: 'quantity' as const,
              key: `quantity__${c.unitId ?? 'unitless'}`,
              unitId: c.unitId,
              header: `${label} (${unitText})`,
              format,
            };
          });
        }
        if (field === 'rate') {
          // Single rate column (per-unit weighted averages are OQ-12): free text wins where seeded;
          // else derive "kr/m3f" from the amount measure's unit + the single real unit column; else bare.
          const soleCode = quantityColumns.length === 1 ? quantityColumns[0].unitCode : null;
          const derived = !m.unit && soleCode && valueUnit ? `${valueUnit}/${soleCode}` : m.unit;
          return [{ kind: 'rate', key: 'rate', header: derived ? `${label} (${derived})` : label, format }];
        }
        return [{ kind: 'amount', key: 'amount', header: m.unit ? `${label} (${m.unit})` : label, format }];
      });
    },
    [measures, quantityColumns, usingFallbackMeasures, t],
  );

  const allRows = useMemo(() => {
    const leaves: RollupLeaf[] = (data?.rows ?? []).map((r) => ({
      keys: r.key.map((k) => k.valueName),
      amount: r.amount,
      quantities: (r.quantities ?? []).map((q) => ({ unitId: q.unitOfMeasureId ?? null, quantity: q.quantity })),
    }));
    return buildRollupRows(leaves);
  }, [data]);

  const visibleRows = allRows.filter((r) => !isHiddenByCollapse(r.path, collapsed));
  const grandTotal = data?.grandTotal ?? 0;
  // Footer: per-unit totals come from the server's honest column sums; a footer Pris exists only when
  // the whole scope is one real-or-unitless bucket ("everything sums" — otherwise it is undefined).
  const soleTotal = quantityColumns.length === 1 ? quantityColumns[0].totalQuantity : null;
  const totalUnitPrice = soleTotal ? grandTotal / soleTotal : null;
  const valueOf = (row: RollupDisplayRow, col: PivotCol) =>
    col.kind === 'quantity'
      ? row.quantities.find((q) => q.unitId === col.unitId)?.quantity ?? null
      : col.kind === 'rate' ? row.unitPrice : row.amount;
  const footerValueOf = (col: PivotCol) =>
    col.kind === 'quantity'
      ? quantityColumns.find((c) => c.unitId === col.unitId)?.totalQuantity ?? null
      : col.kind === 'rate' ? totalUnitPrice : grandTotal;

  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  if (isFetching) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (isError) {
    return (
      <Alert severity="error">
        {error?.message ?? t('Finance.PlanRollup.PivotLoadFailed', { defaultValue: 'Failed to load the dimensional pivot.' })}
      </Alert>
    );
  }
  if (!data) return null;

  return (
    <Box>
      {data.warnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
      {axes.length <= 1 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('Finance.PlanRollup.PivotNoRowDims', {
            defaultValue: 'This Plan View has no row dimensions to pivot by — showing header-type totals only.',
          })}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ maxWidth: 900 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {axes.map((a) => a.name).join(' › ') || t('Finance.PlanEntry.Group', { defaultValue: 'Group' })}
              </TableCell>
              {measureCols.map((mc) => (
                <TableCell key={mc.key} align="right">{mc.header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow key={row.path} hover>
                <TableCell sx={{ pl: row.depth * 2.5 + 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: row.isGroup ? 600 : 400 }}>
                    {row.isGroup ? (
                      <Box component="span" sx={{ display: 'inline-flex', cursor: 'pointer' }} onClick={() => toggle(row.path)}>
                        {collapsed.has(row.path) ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ width: 20, display: 'inline-block' }} />
                    )}
                    <span>{row.label}{row.isGroup ? ` (${row.childCount})` : ''}</span>
                  </Box>
                </TableCell>
                {measureCols.map((mc) => (
                  <TableCell key={mc.key} align="right" sx={{ fontWeight: row.isGroup ? 600 : 400 }}>
                    {fmt(valueOf(row, mc), mc.format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={measureCols.length + 1}>
                  <Typography variant="body2" color="text.secondary">
                    {t('Finance.PlanRollup.PivotEmpty', { defaultValue: 'No budget under this selection for this Plan View.' })}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                {filteredLabel(t('Finance.PlanRollup.Total', { defaultValue: 'Total' }), data.isShaped)}
              </TableCell>
              {measureCols.map((mc) => (
                <TableCell key={mc.key} align="right" sx={{ fontWeight: 600 }}>
                  {fmt(footerValueOf(mc), mc.format)}
                </TableCell>
              ))}
            </TableRow>
            <RollupUnfilteredTotalRow
              isShaped={data.isShaped}
              unfilteredTotal={unfilteredTotal}
              colSpan={measureCols.length + 1}
            />
          </TableFooter>
        </Table>
      </Paper>
    </Box>
  );
}

export default PlanRollupPivot;
